import { Component, Input, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, filter, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { ArticleService } from '../../../../../core/catalog';
import { Article, CartType, SolrSearchResult, SolrSearchSortingDefaults, SolrSearchType } from '../../../../../core/model';
import { SearchService } from '../../../../../core/search';

@Component({
  selector: 'py-my-assortment-article-list',
  templateUrl: './my-assortment-article-list.component.html',
  styleUrls: ['./my-assortment-article-list.component.scss'],
})
export class MyAssortmentArticleListComponent implements OnInit, OnDestroy {
  @Input() query: string;
  @Input() additionalArticleActions?: TemplateRef<any>;
  @Input() cartType: CartType;

  private subscription = new Subscription();

  currentPage = 0;
  searchResults$: Observable<SolrSearchResult>;
  articles$: Observable<Observable<Article>[]>;
  articleRefs$: Observable<string[]>;
  formGroup = new UntypedFormGroup({ text: new UntypedFormControl(''), pageSize: new UntypedFormControl('10') });
  searchText: string;

  get pageSize(): number {
    return parseInt(this.formGroup.get('pageSize')?.value, 10);
  }

  constructor(private searchService: SearchService, private articleService: ArticleService) {}

  get queryIncludingFreeText$(): Observable<string> {
    return this.formGroup.get('text').valueChanges.pipe(
      startWith(''),
      map((searchText) => (searchText ? searchText.concat(this.query) : this.query))
    );
  }

  private search(currentPage: number): void {
    const queryIncludingFreeText = this.searchText ? this.searchText.concat(this.query) : this.query;

    this.searchService.search(queryIncludingFreeText, {
      currentPage,
      pageSize: this.pageSize,
      sort: SolrSearchSortingDefaults.PDP_TYPE_SKU_PICKER,
      searchType: SolrSearchType.ARTICLE,
    });
  }

  ngOnInit(): void {
    this.search(this.currentPage);

    this.searchResults$ = this.queryIncludingFreeText$.pipe(
      switchMap((query) => this.searchService.getResults(SolrSearchType.ARTICLE, query)),
      filter((searchResult) => Object.keys(searchResult).length > 0),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.articleRefs$ = this.searchResults$.pipe(
      map((searchResult) => searchResult.articleRefs || []),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.subscription.add(
      this.articleRefs$.subscribe((articleRefs) => {
        return this.articleService.getArticles(articleRefs || []);
      })
    );

    this.subscription.add(
      this.formGroup.valueChanges.pipe(debounceTime(800)).subscribe(({ text }) => {
        this.searchText = text;
        this.currentPage = 0;

        this.search(this.currentPage);
      })
    );
  }

  nextPage(newPage: number): void {
    this.currentPage = newPage;
    this.search(this.currentPage);
  }

  onResetSearchForm(): void {
    this.formGroup.get('text').reset();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
